import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const emptyForm = {
  week_number: '',
  date_range: '',
  restaurant_name: '',
  address: '',
  logo_url: '',
  semester: '',
  active: true,
};

export default function FeaturedRestaurant() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchWeeks();
  }, []);

  const fetchWeeks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('featured_restaurant')
      .select('*')
      .order('week_number', { ascending: true });
    if (!error) setWeeks(data);
    setLoading(false);
  };

  const handleEdit = (week) => {
    setForm({
      week_number: week.week_number || '',
      date_range: week.date_range || '',
      restaurant_name: week.restaurant_name || '',
      address: week.address || '',
      logo_url: week.logo_url || '',
      semester: week.semester || '',
      active: week.active,
    });
    setEditingId(week.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this week?')) return;
    await supabase.from('featured_restaurant').delete().eq('id', id);
    fetchWeeks();
  };

  const handleToggle = async (week) => {
    await supabase.from('featured_restaurant').update({ active: !week.active }).eq('id', week.id);
    fetchWeeks();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.week_number) {
      alert('Please enter a week number.');
      return;
    }
    setSaving(true);

    const payload = {
      week_number: parseInt(form.week_number),
      date_range: form.date_range || null,
      restaurant_name: form.restaurant_name || null,
      address: form.address || null,
      logo_url: form.logo_url || null,
      semester: form.semester || null,
      active: form.active,
    };

    if (editingId) {
      const { error } = await supabase.from('featured_restaurant').update(payload).eq('id', editingId);
      if (error) { alert('Error: ' + error.message); setSaving(false); return; }
      setSuccessMsg('Week updated!');
    } else {
      const { error } = await supabase.from('featured_restaurant').insert(payload);
      if (error) { alert('Error: ' + error.message); setSaving(false); return; }
      setSuccessMsg('Week added!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchWeeks();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  // Group by semester
  const semesters = [...new Set(weeks.map(w => w.semester).filter(Boolean))];
  const noSemester = weeks.filter(w => !w.semester);

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Featured Restaurant
          <span style={styles.topbarCount}>{weeks.length} weeks</span>
        </h1>
        <button style={styles.topbarBtn} onClick={() => {
          if (showForm) { handleCancel(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Cancel' : '+ Add Week'}
        </button>
      </div>

      <div style={styles.content}>

        {successMsg && <div style={styles.successBanner}>✅ {successMsg}</div>}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{editingId ? 'Edit Week' : 'Add New Week'}</h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>WEEK NUMBER *</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    max="17"
                    placeholder="e.g. 1"
                    value={form.week_number}
                    onChange={(e) => setForm({ ...form, week_number: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>SEMESTER</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. Fall 2026"
                    value={form.semester}
                    onChange={(e) => setForm({ ...form, semester: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>DATE RANGE</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="e.g. Aug 25 – Aug 31, 2026"
                  value={form.date_range}
                  onChange={(e) => setForm({ ...form, date_range: e.target.value })}
                />
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>RESTAURANT NAME</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. Illegal Pete's (or leave blank for break week)"
                    value={form.restaurant_name}
                    onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })}
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>ADDRESS</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. 1447 Pearl St, Boulder"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>LOGO URL</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="https://... (Supabase storage URL)"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                />
                {form.logo_url && (
                  <img src={form.logo_url} alt="Preview" style={styles.logoPreview} />
                )}
                <div style={styles.fieldHint}>Leave blank for break weeks</div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    style={{ marginRight: '8px' }}
                  />
                  Active — show on website
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Week'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
              </div>

            </form>
          </div>
        )}

        {/* WEEKS TABLE */}
        {weeks.length === 0 ? (
          <div style={styles.empty}>No weeks yet. Click "+ Add Week" to get started.</div>
        ) : (
          <>
            {/* Group by semester if available */}
            {semesters.length > 0 ? (
              <>
                {semesters.map(semester => (
                  <div key={semester} style={styles.semesterSection}>
                    <div style={styles.semesterHeader}>
                      <h2 style={styles.semesterTitle}>{semester}</h2>
                      <span style={styles.semesterCount}>
                        {weeks.filter(w => w.semester === semester).length} weeks
                      </span>
                    </div>
                    <WeeksTable
                      weeks={weeks.filter(w => w.semester === semester)}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  </div>
                ))}
                {noSemester.length > 0 && (
                  <div style={styles.semesterSection}>
                    <div style={styles.semesterHeader}>
                      <h2 style={styles.semesterTitle}>No Semester</h2>
                    </div>
                    <WeeksTable
                      weeks={noSemester}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  </div>
                )}
              </>
            ) : (
              <WeeksTable
                weeks={weeks}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            )}
          </>
        )}

      </div>
    </div>
  );
}

function WeeksTable({ weeks, onEdit, onDelete, onToggle }) {
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {['Week', 'Date Range', 'Logo', 'Restaurant', 'Address', 'Active', 'Actions'].map((h) => (
              <th key={h} style={styles.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.id} style={week.restaurant_name ? {} : styles.breakRow}>
              <td style={styles.td}>
                <div style={styles.weekBadge}>W{week.week_number}</div>
              </td>
              <td style={styles.td}>{week.date_range || '—'}</td>
              <td style={styles.td}>
                {week.logo_url ? (
                  <img src={week.logo_url} alt={week.restaurant_name} style={styles.logoThumb} />
                ) : (
                  <div style={styles.noLogo}>—</div>
                )}
              </td>
              <td style={styles.td}>
                {week.restaurant_name || (
                  <span style={styles.breakLabel}>Break Week</span>
                )}
              </td>
              <td style={styles.td}>{week.address || '—'}</td>
              <td style={styles.td}>
                <Toggle value={week.active} onChange={() => onToggle(week)} />
              </td>
              <td style={styles.td}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={styles.editBtn} onClick={() => onEdit(week)}>Edit</button>
                  <button style={styles.deleteBtn} onClick={() => onDelete(week.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: value ? GOLD : '#ddd', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', top: '3px', left: value ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
  topbarCount: { fontSize: '13px', fontWeight: '400', color: '#aaa' },
  topbarBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  content: { padding: '24px', flex: 1 },
  successBanner: { background: '#f0fff4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#166534', fontSize: '14px' },
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '14px', padding: '24px', marginBottom: '24px' },
  formTitle: { fontSize: '16px', fontWeight: '500', color: '#111', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formRow: { marginBottom: '16px' },
  label: { display: 'block', color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  fieldHint: { color: '#bbb', fontSize: '11px', marginTop: '4px' },
  logoPreview: { width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e0e0e0', marginTop: '8px', background: '#f9f9f9' },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: '#444', cursor: 'pointer' },
  submitBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', flex: 1 },
  cancelBtn: { background: 'none', color: '#888', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', cursor: 'pointer' },
  semesterSection: { marginBottom: '28px' },
  semesterHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  semesterTitle: { fontSize: '15px', fontWeight: '500', color: '#111', margin: 0 },
  semesterCount: { fontSize: '12px', color: '#aaa' },
  tableWrap: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { background: '#f9f9f9', color: '#888', fontSize: '11px', padding: '10px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontWeight: '500' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', color: '#111', verticalAlign: 'middle' },
  breakRow: { background: '#fafafa' },
  weekBadge: { background: 'rgba(255,194,32,0.12)', color: '#b8860b', fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '20px', display: 'inline-block' },
  logoThumb: { width: '36px', height: '36px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#fff' },
  noLogo: { color: '#ccc', fontSize: '13px' },
  breakLabel: { color: '#aaa', fontStyle: 'italic', fontSize: '12px' },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#444', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ff6b6b', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px' },
};
